import Head from 'next/head';
import { useMemo } from 'react';

export interface BaseSchema {
  '@context'?: string;
  '@type': string;
}

export interface UnitPriceSpecification {
  '@type': 'UnitPriceSpecification';
  priceCurrency: 'GBP';
  price: string;
  priceType?: 'https://schema.org/StrikethroughPrice';
}

export interface Offers {
  '@type': 'Offer';
  priceCurrency: 'GBP';
  price: string;
  priceValidUntil?: string;
  itemCondition: 'http://schema.org/NewCondition';
  availability: 'http://schema.org/InStock' | 'http://schema.org/OutOfStock';
  priceSpecification?: UnitPriceSpecification;
}

export interface ProductSchema extends BaseSchema {
  '@type': 'Product';
  name: string;
  image?: string;
  description: string;
  brand: {
    '@type': 'Brand';
    name: string;
  };
  offers: Offers;
  aggregateRating?: {
    '@type': 'AggregateRating';
    ratingValue: string;
    reviewCount: string;
  };
  seller: {
    '@type': 'Organization';
    name: 'Waitrose';
  };
}

export interface ItemListElement {
  '@type': 'ListItem';
  position: number;
  name: string;
  item: string;
}
export interface BreadCrumbSchema extends BaseSchema {
  '@type': 'BreadcrumbList';
  itemListElement: ItemListElement[];
}

export type Schema = BaseSchema | ProductSchema | BreadCrumbSchema;

export interface JsonLDSchemaProps<T> {
  schema: T | null;
  nonce?: string;
}

function JsonLDSchema<T extends Schema>({ nonce, schema }: JsonLDSchemaProps<T>) {
  const finalJsonLD: T | null = useMemo(() => {
    if (schema) {
      return {
        '@context': 'https://schema.org',
        ...schema,
      };
    }
    return null;
  }, [schema]);

  return finalJsonLD ? (
    <Head>
      <script
        dangerouslySetInnerHTML={{ __html: JSON.stringify(finalJsonLD) }}                                                                                         
        nonce={nonce}
        type="application/ld+json"
      />
    </Head>
  ) : null;
}

export default JsonLDSchema;
